import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronRight } from "lucide-react";

const POSITIONS = [
  { id: "Goalkeeper", label: "GK" },
  { id: "Centre-Back", label: "CB" },
  { id: "Left-Back", label: "LB" },
  { id: "Right-Back", label: "RB" },
  { id: "Defensive Midfielder", label: "CDM" },
  { id: "Central Midfielder", label: "CM" },
  { id: "Attacking Midfielder", label: "CAM" },
  { id: "Left Winger", label: "LW" },
  { id: "Right Winger", label: "RW" },
  { id: "Striker", label: "ST" },
  { id: "Centre-Forward", label: "CF" },
];

const STRENGTHS = [
  "Pace & Acceleration", "Dribbling & Ball Control", "Passing & Vision",
  "Shooting & Finishing", "Heading & Aerial", "Tackling & Defending",
  "Physical Strength", "Leadership", "Positioning & Movement", "Set Pieces",
];

const PLAYER_TYPES = [
  "Target Striker", "Playmaker", "Box-to-Box", "Ball Winner",
  "Creative Winger", "Pace Winger", "Ball-Playing Defender",
  "Physical Defender", "Shot-Stopper", "Sweeper Keeper",
];

export default function MicroOnboarding() {
  const [step, setStep] = useState(0);
  const [position, setPosition] = useState("");
  const [strengths, setStrengths] = useState([]);
  const [playerType, setPlayerType] = useState("");
  const navigate = useNavigate();

  const toggleStrength = (s) => {
    setStrengths((prev) =>
      prev.includes(s) ? prev.filter((x) => x !== s) : prev.length < 3 ? [...prev, s] : prev
    );
  };

  const handleFinish = () => {
    localStorage.setItem("micro_onboarding", JSON.stringify({ position, strengths, playerType }));
    navigate("/register");
  };

  const posLabel = POSITIONS.find((p) => p.id === position)?.label || position;
  const previewLine = position && playerType
    ? `${playerType} operating from the ${position} position with strong ${strengths.slice(0, 2).join(" and ").toLowerCase()}.`
    : "";

  return (
    <div className="bg-[#0A0A0A] min-h-screen flex items-center justify-center px-4" data-testid="micro-onboarding">
      <div className="w-full max-w-lg">
        {/* Continuity header */}
        <div className="text-center mb-8" data-testid="start-header">
          <h1 className="text-2xl sm:text-3xl font-black tracking-tighter uppercase text-[#F5F5F5] mb-2">
            Let's build your profile properly
          </h1>
          <p className="text-sm text-[#777]">Takes a few minutes. Fully guided.</p>
        </div>

        {/* Progress */}
        <div className="flex gap-1.5 mb-10">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className={`h-1 flex-1 rounded-sm transition-all duration-500 ${i <= step ? "bg-[#D4AF37]" : "bg-[#262626]"}`} />
          ))}
        </div>

        {/* Step 0: Position */}
        {step === 0 && (
          <div className="animate-fade-in" data-testid="step-position">
            <h2 className="text-2xl sm:text-3xl font-black tracking-tighter uppercase text-[#F5F5F5] mb-2">
              What position do you play?
            </h2>
            <p className="text-sm text-[#A3A3A3] mb-8">Select your primary position</p>
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
              {POSITIONS.map((p) => (
                <button
                  key={p.id}
                  onClick={() => { setPosition(p.id); setTimeout(() => setStep(1), 250); }}
                  className={`py-3 px-2 text-xs font-bold uppercase tracking-wider rounded-sm transition-all ${
                    position === p.id
                      ? "bg-[#D4AF37] text-black"
                      : "bg-[#1A1A1A] text-[#A3A3A3] border border-[#262626] hover:border-[#D4AF37]/50 hover:text-[#F5F5F5]"
                  }`}
                  data-testid={`pos-${p.label}`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 1: Strengths */}
        {step === 1 && (
          <div className="animate-fade-in" data-testid="step-strengths">
            <div className="text-xs text-[#D4AF37] font-bold mb-3">Got it — {position}.</div>
            <h2 className="text-2xl sm:text-3xl font-black tracking-tighter uppercase text-[#F5F5F5] mb-2">
              What are you best at?
            </h2>
            <p className="text-sm text-[#A3A3A3] mb-8">Select up to 3 strengths</p>
            <div className="grid grid-cols-2 gap-2 mb-8">
              {STRENGTHS.map((s) => (
                <button
                  key={s}
                  onClick={() => toggleStrength(s)}
                  className={`py-3 px-3 text-xs font-semibold rounded-sm transition-all text-left ${
                    strengths.includes(s)
                      ? "bg-[#D4AF37] text-black"
                      : "bg-[#1A1A1A] text-[#A3A3A3] border border-[#262626] hover:border-[#D4AF37]/50 hover:text-[#F5F5F5]"
                  }`}
                  data-testid={`strength-${s.split(" ")[0]}`}
                >
                  {s}
                </button>
              ))}
            </div>
            {strengths.length > 0 && (
              <button onClick={() => setStep(2)} className="btn-gold w-full flex items-center justify-center gap-2" data-testid="strengths-continue">
                CONTINUE <ChevronRight size={16} />
              </button>
            )}
          </div>
        )}

        {/* Step 2: Player Type */}
        {step === 2 && (
          <div className="animate-fade-in" data-testid="step-player-type">
            <div className="text-xs text-[#D4AF37] font-bold mb-3">Strong in {strengths[0]}. Noted.</div>
            <h2 className="text-2xl sm:text-3xl font-black tracking-tighter uppercase text-[#F5F5F5] mb-2">
              What type of player are you?
            </h2>
            <p className="text-sm text-[#A3A3A3] mb-8">Pick the one that fits best</p>
            <div className="grid grid-cols-2 gap-2">
              {PLAYER_TYPES.map((t) => (
                <button
                  key={t}
                  onClick={() => { setPlayerType(t); setTimeout(() => setStep(3), 250); }}
                  className={`py-3 px-3 text-xs font-bold uppercase tracking-wider rounded-sm transition-all ${
                    playerType === t
                      ? "bg-[#D4AF37] text-black"
                      : "bg-[#1A1A1A] text-[#A3A3A3] border border-[#262626] hover:border-[#D4AF37]/50 hover:text-[#F5F5F5]"
                  }`}
                  data-testid={`type-${t.replace(/\s/g, "-")}`}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 3: Preview */}
        {step === 3 && (
          <div className="animate-fade-in" data-testid="step-preview">
            <div className="overline text-center mb-6">YOUR PROFILE IS TAKING SHAPE</div>
            <div className="card-dark p-8 mb-8 text-center">
              <div className="badge-gold inline-block mb-4">{position}</div>
              <p className="text-[#D4AF37] font-semibold mb-5 leading-relaxed">{previewLine}</p>
              <div className="flex flex-wrap justify-center gap-2">
                {strengths.map((s) => (
                  <span key={s} className="text-[10px] bg-[#1A1A1A] text-[#A3A3A3] border border-[#262626] px-2.5 py-1 rounded-sm uppercase tracking-wider">{s}</span>
                ))}
              </div>
            </div>
            <button onClick={handleFinish} className="btn-gold w-full text-base py-4 flex items-center justify-center gap-2" data-testid="unlock-profile">
              UNLOCK MY FULL PROFILE <ChevronRight size={18} />
            </button>
            <p className="text-center text-xs text-[#A3A3A3] mt-4">Takes less than 5 minutes to complete</p>
          </div>
        )}
      </div>
    </div>
  );
}
